import { useState, useEffect } from 'react';
import api, { formatINR } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { FileText, Users, Store, IndianRupee, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS = {
  Draft: 'bg-slate-100 text-slate-700',
  Sent: 'bg-blue-100 text-blue-700',
  Approved: 'bg-green-100 text-green-700',
  Order: 'bg-amber-100 text-amber-700',
  Delivered: 'bg-purple-100 text-purple-700',
  Installed: 'bg-emerald-100 text-emerald-700',
};

// Navy + Gold palette aligned with PDF brand
const DONUT_COLORS = ['#0B2545', '#D4AF37', '#1a365d', '#8B6914', '#475569'];

function StatSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[0, 1, 2, 3].map(i => (
        <Card key={i} className="border border-slate-200 animate-pulse">
          <CardContent className="p-5">
            <div className="h-3 bg-slate-200 rounded w-20 mb-3"></div>
            <div className="h-7 bg-slate-200 rounded w-28"></div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/dashboard/stats')
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-page">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Loading your business overview...</p>
        </div>
        <StatSkeleton />
        <Card className="border border-slate-200 animate-pulse"><CardContent className="p-5 h-64 bg-slate-50"></CardContent></Card>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-slate-400" data-testid="dashboard-page">
        <TrendingUp size={48} className="opacity-30 mb-3" />
        <p className="text-sm">Unable to load dashboard data. Please refresh.</p>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Quotations', value: stats.total_quotations, icon: FileText, color: 'text-[#0B2545]' },
    { label: 'Pipeline Value', value: formatINR(stats.total_value), icon: IndianRupee, color: 'text-[#D4AF37]' },
    { label: 'Active Customers', value: stats.total_customers, icon: Users, color: 'text-emerald-600' },
    { label: 'Vendors', value: stats.total_vendors, icon: Store, color: 'text-purple-600' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Overview of your business</p>
        </div>
        <button
          onClick={() => navigate('/quotations/new')}
          className="bg-[#D4AF37] hover:bg-[#b8962f] text-[#0B2545] font-semibold px-4 py-2 rounded-lg text-sm transition-colors shadow-sm"
          data-testid="new-quotation-btn"
        >
          + New Quotation
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <Card key={i} className="border border-slate-200 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200" data-testid={`stat-card-${i}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">{s.label}</p>
                  <p className={`text-2xl font-semibold mt-1 font-['Outfit'] ${s.color}`}>{s.value}</p>
                </div>
                <div className={`p-2.5 rounded-lg bg-slate-50 ${s.color}`}>
                  <s.icon size={20} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Area Chart (trend) */}
        <Card className="lg:col-span-2 border border-slate-200" data-testid="monthly-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
              <TrendingUp size={18} className="text-[#D4AF37]" /> Monthly Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.monthly?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={stats.monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pipelineGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.55} />
                      <stop offset="95%" stopColor="#D4AF37" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip
                    formatter={(v) => formatINR(v)}
                    contentStyle={{ border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '12px' }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#0B2545" strokeWidth={2} fill="url(#pipelineGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[250px] text-slate-400 text-sm">
                <TrendingUp size={40} className="opacity-20 mb-2" />
                No data yet. Create your first quotation!
              </div>
            )}
          </CardContent>
        </Card>

        {/* Vendor Donut Chart */}
        <Card className="border border-slate-200" data-testid="vendor-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Outfit']">Vendor Mix</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.vendor_distribution?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.vendor_distribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    label={({ name }) => name?.slice(0, 10)}
                  >
                    {stats.vendor_distribution.map((_, i) => (
                      <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => formatINR(v)}
                    contentStyle={{ border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[250px] text-slate-400 text-sm">
                <Store size={40} className="opacity-20 mb-2" />
                No vendor data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Quotations */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-['Outfit']">Recent Quotations</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.recent_quotations?.length > 0 ? (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm" data-testid="recent-quotations-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Quote #</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Customer</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                      <th className="text-right px-4 py-3 font-medium text-slate-600">Total</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_quotations.map((q) => (
                      <tr
                        key={q.id}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/quotations/${q.id}`)}
                      >
                        <td className="px-4 py-3 font-mono text-xs font-medium text-[#0B2545]">{q.quote_number}</td>
                        <td className="px-4 py-3">{q.customer_name || '-'}</td>
                        <td className="px-4 py-3 text-slate-500">{q.date?.slice(0, 10)}</td>
                        <td className="px-4 py-3 text-right font-medium">{formatINR(q.grand_total)}</td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className={STATUS_COLORS[q.status] || ''}>{q.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile stacked cards */}
              <div className="sm:hidden space-y-2">
                {stats.recent_quotations.map((q) => (
                  <div
                    key={q.id}
                    onClick={() => navigate(`/quotations/${q.id}`)}
                    className="border border-slate-200 rounded-lg p-3 cursor-pointer hover:bg-slate-50 active:bg-slate-100 transition-colors"
                    data-testid={`recent-quotation-mobile-${q.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-xs font-semibold text-[#0B2545]">{q.quote_number}</span>
                      <Badge variant="secondary" className={STATUS_COLORS[q.status] || ''}>{q.status}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{q.customer_name || '-'}</p>
                        <p className="text-[11px] text-slate-400">{q.date?.slice(0, 10)}</p>
                      </div>
                      <p className="text-sm font-semibold text-slate-900">{formatINR(q.grand_total)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <FileText size={40} className="opacity-20 mb-3" />
              <p className="text-sm mb-4">No quotations yet.</p>
              <button
                onClick={() => navigate('/quotations/new')}
                className="bg-[#0B2545] hover:bg-[#0a1f3a] text-white text-xs font-medium px-4 py-2 rounded-lg"
              >
                Create Your First Quotation
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
